import { Row, createTable, getCoreRowModel } from "@tanstack/react-table";
import React from "react";

import { renderFilePreviewer } from "./DriveUtils";

import EmptyState from "@/components/reusables/EmptyState";
import { CustomDropdown } from "@/components/reusables/custom-action-button";
import Paginator from "@/components/reusables/data-table/paginator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { DRIVE_CONSTANTS } from "@/constants/Drive.constant";
import { ONE, TEN, ZERO } from "@/constants/constants";
import { useDrive } from "@/context/DriveContext";
import { IDrive } from "@/interfaces/project-register.interface";
import { cn } from "@/lib/utils";
import Icons from "@/utils/Icons";
import { dateFormat } from "@/utils/helper-functions/date-time.helper";
import { getFileSize } from "@/utils/helpers";

export const DriveGridView: React.FC = () => {
  const {
    data,
    folderId,
    activeFolderTarget,
    getActionOptions,
    setFolderId,
    previousDir,
    resetFolderId,
    handleDragStart,
    handleDragEnd,
    handleDrop,
    isDragging,
    isMoving,
    isCancelled,
    draggedItem,
    pagination,
    setLocalPagination,
    handleFileNavigation,
    searchQuery,
    handleSearch,
  } = useDrive();

  // Create a mock table for the paginator
  const table = React.useMemo(() => {
    const tableInstance = createTable({
      data: data || [],
      columns: [],
      state: {
        pagination: {
          pageSize: pagination?.limit || TEN,
          pageIndex: (pagination?.currentPage || ONE) - ONE,
        },
      },
      onPaginationChange: (updater) => {
        if (typeof updater === "function") {
          const newState = updater({
            pageSize: pagination?.limit || TEN,
            pageIndex: (pagination?.currentPage || ONE) - ONE,
          });
          if (pagination?.handleLimitUpdate) {
            pagination.handleLimitUpdate(newState.pageSize);
          }
        }
      },
      getCoreRowModel: getCoreRowModel(),
      // eslint-disable-next-line no-empty-function
      onStateChange: () => {},
      renderFallbackValue: null,
    });

    return tableInstance;
  }, [data, pagination]);

  // Extract click handler functions

  /**
   * Reset pagination state
   */
  const resetPagination = React.useCallback(() => {
    setLocalPagination({
      limit: TEN,
      offset: ZERO,
      currentPage: ONE,
    });
  }, [setLocalPagination]);

  /**
   * Handle back navigation
   */
  const handleBackNavigation = React.useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault();
      }

      // Navigate to previous directory or root
      if (previousDir) {
        setFolderId(previousDir);
      } else {
        resetFolderId();
      }

      // Reset pagination
      resetPagination();

      // If searching, clear the search
      if (searchQuery) {
        handleSearch("");
      }
    },
    [
      previousDir,
      setFolderId,
      resetFolderId,
      resetPagination,
      searchQuery,
      handleSearch,
    ],
  );

  /**
   * Handle back item click
   */
  const handleBackItemClick = React.useCallback(() => {
    handleFileNavigation({
      url: "",
      isEmpty: true,
      folderId,
      previousDir,
    });
    resetPagination();
  }, [handleFileNavigation, folderId, previousDir, resetPagination]);

  /**
   * Handle folder or file click
   */
  const handleItemClick = React.useCallback(
    (item: IDrive) => {
      const isFolder = item.type === "dir";

      if (isFolder) {
        setFolderId(item.id);
        resetPagination();
      }
    },
    [setFolderId, resetPagination],
  );

  /**
   * Handle drag operations
   */
  const handleDragOperations = React.useCallback(
    (item: IDrive) => ({
      onDragStart: (e: React.DragEvent): void => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", item.id);
        handleDragStart(item);
      },
      onDragEnd: handleDragEnd,
      onDragOver: (e: React.DragEvent): void => {
        const isFolder = item.type === "dir";
        const isDraggedItem = draggedItem?.id === item.id;
        const isBackItem = item.empty;

        if ((isFolder || isBackItem) && !isDraggedItem) {
          e.preventDefault(); // Allow drop
        }
      },
      onDrop: (e: React.DragEvent): void => {
        e.preventDefault();
        const isFolder = item.type === "dir";
        const isDraggedItem = draggedItem?.id === item.id;
        const isBackItem = item.empty;

        if ((isFolder || isBackItem) && !isDraggedItem) {
          handleDrop(item);
        }
      },
    }),
    [handleDragStart, handleDragEnd, handleDrop, draggedItem],
  );

  const renderGridItem = (item: IDrive, index: number): JSX.Element => {
    const isFolder = item.type === "dir";
    const isHighlighted = activeFolderTarget === item.id;
    const isDraggedItem = draggedItem?.id === item.id;
    const isBackItem = item.empty;
    const isDropTarget =
      isDragging && (isFolder || isBackItem) && !isDraggedItem;

    const dragHandlers = handleDragOperations(item);

    // Handle back navigation item
    if (isBackItem) {
      return (
        <div
          key={`back-item-${index}`}
          className={cn(
            "p-3 rounded-md border-gray-200 border-2 border-dashed hover:bg-gray-50 cursor-pointer flex items-center justify-center",
            isDropTarget && "bg-blue-50 border-blue-300",
          )}
          onClick={handleBackItemClick}
          onDragOver={dragHandlers.onDragOver}
          onDrop={dragHandlers.onDrop}
        >
          <div className='text-sm text-gray-500 font-medium text-center'>
            <Icons name='arrow-back' size={20} />
            Back
          </div>
        </div>
      );
    }

    return (
      <div
        key={item.id || `item-${index}`}
        className={cn(
          "p-4 rounded-md border border-gray-200 hover:shadow-md transition-all flex flex-col justify-start relative",
          isHighlighted && "bg-blue-50 border-blue-200",
          isDraggedItem && "opacity-50 border-blue-400",
          isFolder ? "cursor-pointer" : "cursor-default",
          isDropTarget && "bg-blue-50 border-blue-300 shadow-md",
        )}
        onClick={() => handleItemClick(item)}
        draggable={!isBackItem}
        onDragStart={dragHandlers.onDragStart}
        onDragEnd={dragHandlers.onDragEnd}
        onDragOver={dragHandlers.onDragOver}
        onDrop={dragHandlers.onDrop}
      >
        <div className='w-16 h-full flex justify-start mb-2'>
          {isFolder ? (
            <Icons name='folder' size={30} />
          ) : (
            renderFilePreviewer(
              item,
              () => handleItemClick(item),
              "w-full h-full object-cover",
            )
          )}
        </div>

        <div className='text-left'>
          <div className='h-auto max-w-full font-medium text-sm mb-2 break-words line-clamp-1'>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>{item.name}</div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{item.name}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <>
            <div className='text-xs max-w-full text-gray-500 mt-1'>
              {isFolder ? "" : getFileSize(item.size || ZERO)}
            </div>
            <div className='flex items-center justify-between'>
              {item.date && (
                <div className='text-xs text-gray-400 mt-1 truncate max-w-[120px]'>
                  {dateFormat(item.date, "dd MMM yyyy")}
                </div>
              )}
              {item.uploader && (
                <div className='text-xs text-gray-400 mt-1 truncate max-w-full'>
                  {`${item.uploader.firstName} ${item.uploader.lastName || ""}`}
                </div>
              )}
            </div>
          </>
        </div>

        {!isDraggedItem && (
          <div
            className='absolute top-2 right-2'
            onClick={(e) => e.stopPropagation()}
          >
            <CustomDropdown
              data={getActionOptions({ original: item } as Row<IDrive>)}
            />
          </div>
        )}
      </div>
    );
  };

  const renderContent = (): JSX.Element => {
    if (isMoving) {
      return (
        <div className='relative'>
          <div className='absolute inset-0 bg-white/70 z-10 flex items-center justify-center'>
            <div className='flex flex-col items-center'>
              <Icons name='cloud-upload-2' size={32} />
              <p className='text-gray-700'>
                {isCancelled ? "Cancelling operation..." : "Moving item..."}
              </p>
            </div>
          </div>

          <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 p-2'>
            {data.length > ZERO ? (
              data.map((item, index) => renderGridItem(item, index))
            ) : (
              <EmptyState
                title='No items found'
                message='Upload files or create a new folder'
              />
            )}
          </div>
        </div>
      );
    }

    // Check if we're currently searching
    const isSearching = searchQuery.length > 0;

    return (
      <div className='p-2'>
        <div
          className={cn(
            "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4",
            isDragging && "outline outline-2 outline-blue-200 rounded-md",
          )}
        >
          {data.length > ZERO && !data.every((item) => item.empty) ? (
            data.map((item, index) => renderGridItem(item, index))
          ) : (
            <>
              {folderId && !isSearching && (
                <a
                  href='#'
                  className='flex items-center gap-1 hover:underline'
                  onClick={handleBackNavigation}
                >
                  <Icons name='arrow-back' />
                  <span>Back</span>
                </a>
              )}

              <EmptyState
                icon='folder'
                title={
                  isSearching
                    ? `No results for "${searchQuery}"`
                    : "No items found"
                }
                message={
                  isSearching
                    ? DRIVE_CONSTANTS.MESSAGES.TRY_DIFFERENT_KEYWORD
                    : DRIVE_CONSTANTS.MESSAGES.UPLOAD_NEW_FILE
                }
              />
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className='flex flex-col'>
      {renderContent()}

      {/* Pagination section */}
      {pagination && (pagination.totalResult || ZERO) > TEN && (
        <div className='flex justify-end'>
          <Paginator
            table={table}
            currentPage={pagination?.currentPage || ONE}
            totalPages={pagination?.totalPages || ONE}
            onPageChange={(pageNumber: number) => {
              if (pagination?.handlePageUpdate) {
                pagination.handlePageUpdate(pageNumber);
              }
            }}
            showPreviousNext
            pagination={pagination}
          />
        </div>
      )}
    </div>
  );
};

export default DriveGridView;
